import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { quizAPI } from '../lib/api';
import DdgCaptcha from './DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../lib/ddgCaptcha';
import PersonalDataConsent from './PersonalDataConsent';
import MarketingConsent from './MarketingConsent';
import {
  COMPANY_WEBSITE_LABEL,
  QUESTION_LABEL,
  WORK_EMAIL_LABEL,
  normalizeCompanyWebsite,
} from '../lib/formFieldSchemas';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM_DATA = {
  patientsVolume: '',
  problems: [],
  expectedResults: [],
  phone: '',
  name: '',
  email: '',
  companyWebsite: '',
  question: '',
  consent: false,
  marketingConsent: false,
};

const QuizForm = ({
  open,
  onOpenChange,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setFormData({ ...INITIAL_FORM_DATA });
    } else {
      setCaptchaToken('');
    }
  }, [open]);

  const totalSteps = 4; // 3 вопроса + контактные данные

  const questions = [
    {
      id: 1,
      question: 'Сколько клиентов вы обслуживаете ежемесячно?',
      type: 'radio',
      options: [
        { value: 'less-500', label: 'Менее 100' },
        { value: '500-1000', label: 'до 500' },
        { value: 'more-1000', label: 'Более 500' },
      ],
    },
    {
      id: 2,
      question: 'С какими проблемами вы сталкиваетесь в текущей системе?',
      type: 'checkbox',
      options: [
        { value: 'missed-calls', label: 'Высокая доля ошибок в ручной работе' },
        { value: 'long-wait', label: 'Длительное ожидание результата' },
        { value: 'data-analysis', label: 'Трудности в анализе данных' },
        { value: 'resource-allocation', label: 'Неэффективное распределение ресурсов персонала' },
      ],
    },
    {
      id: 3,
      question: 'Какие результаты вы ожидаете от внедрения системы?',
      type: 'checkbox',
      options: [
        { value: 'increase-patients', label: 'Увеличение финансовых показателей на 15-30%' },
        { value: 'reduce-wait', label: 'Сокращение времени ожидания результата' },
        { value: 'improve-quality', label: 'Улучшение качества процессов' },
        { value: 'optimize-staff', label: 'Высвобождение времени у персонала' },
      ],
    },
  ];

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRadioChange = (value) => {
    const question = questions[currentStep - 1];
    if (question.id === 1) {
      setFormData({
        ...formData,
        patientsVolume: value,
      });
    }
  };

  const handleCheckboxChange = (value, checked) => {
    const question = questions[currentStep - 1];
    const fieldName = question.id === 2 ? 'problems' : 'expectedResults';
    
    setFormData({
      ...formData,
      [fieldName]: checked
        ? [...formData[fieldName], value]
        : formData[fieldName].filter((item) => item !== value),
    });
  };

  const isCompanyWebsiteValid = (value) => /\./.test(String(value || '').trim());

  const isStepValid = () => {
    if (currentStep === totalSteps) {
      const hasEmail = emailRegex.test(formData.email.trim());
      return (
        hasEmail &&
        isCompanyWebsiteValid(formData.companyWebsite) &&
        formData.consent &&
        Boolean(captchaToken)
      );
    }

    return true;
  };

  const handleSkip = () => {
    // Пропустить все вопросы и перейти к контактам
    setCurrentStep(totalSteps);
  };

  const handleSubmit = async () => {
    if (!captchaToken) {
      alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
      return;
    }
    if (!formData.consent) {
      alert('Необходимо согласие на обработку персональных данных');
      return;
    }
    if (!emailRegex.test(formData.email.trim())) {
      alert('Укажите корректную рабочую почту');
      return;
    }
    if (!isCompanyWebsiteValid(formData.companyWebsite)) {
      alert('Укажите корректный адрес сайта компании');
      return;
    }
    setIsSubmitting(true);
    try {
      const phone = formData.phone.trim() || null;
      
      // Формируем данные квиза для отправки с читаемыми значениями
      const getQuestionLabel = (questionId, value) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return value;
        const option = question.options.find(opt => opt.value === value);
        return option ? option.label : value;
      };
      
      const quizData = {
        patientsVolume: formData.patientsVolume ? getQuestionLabel(1, formData.patientsVolume) : null,
        problems: formData.problems.map(p => getQuestionLabel(2, p)),
        expectedResults: formData.expectedResults.map(r => getQuestionLabel(3, r)),
        quizType: 'clinic-qualification',
        submittedAt: new Date().toISOString(),
      };
      
      await quizAPI.submitQuiz(
        phone,
        quizData,
        formData.name.trim() || null,
        formData.email.trim(),
        window.location.href,
        normalizeCompanyWebsite(formData.companyWebsite),
        formData.consent,
        formData.marketingConsent,
        formData.question.trim() || null,
        captchaToken,
      );
      
      // Сохраняем данные квиза в localStorage для возможного использования
      localStorage.setItem('quizData', JSON.stringify({
        ...formData,
        submittedAt: new Date().toISOString(),
        quizType: 'clinic-qualification',
      }));
      
      alert('Спасибо за прохождение квиза! Мы свяжемся с вами в ближайшее время.');
      onOpenChange(false);
      // Сброс формы
      setCurrentStep(1);
      setFormData({ ...INITIAL_FORM_DATA });
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert(error.message || 'Произошла ошибка. Попробуйте позже.');
      captchaRef.current?.reset();
      setCaptchaToken('');
      // Не сбрасываем форму при ошибке, чтобы пользователь мог попробовать снова
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = currentStep <= questions.length ? questions[currentStep - 1] : null;
  const progress = (currentStep / totalSteps) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Получить консультацию эксперта</DialogTitle>
          <DialogDescription>
            Заполнение займет 2 минуты. Вы можете пропустить вопросы и сразу оставить контакты.
          </DialogDescription>
        </DialogHeader>

        {/* Прогресс-бар */}
        <div className="space-y-2 mb-6">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Вопрос {currentStep} из {totalSteps}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Навигация по шагам */}
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <div
              key={idx}
              className={`h-2 w-2 rounded-full transition-colors ${
                idx + 1 === currentStep
                  ? 'bg-accent'
                  : idx + 1 < currentStep
                  ? 'bg-accent/50'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Контент вопроса */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Изображение */}
          <div className="hidden md:block">
            <div className="h-full bg-gradient-to-br from-accent/10 to-accent/5 rounded-lg flex items-center justify-center min-h-[300px]">
              <img
                src="/images/solutions.jpeg"
                alt="Решение"
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Вопрос и варианты ответов или контактные данные */}
          <div className="space-y-4">
            {currentStep === totalSteps ? (
              <>
                <h3 className="text-xl font-semibold mb-4">Оставьте контактные данные</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Имя (необязательно)</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ваше имя"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Телефон *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+7 (999) 123-45-67"
                      type="tel"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{WORK_EMAIL_LABEL} *</Label>
                    <Input
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@company.ru"
                      type="email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quiz-company-website">{COMPANY_WEBSITE_LABEL} *</Label>
                    <Input
                      id="quiz-company-website"
                      value={formData.companyWebsite}
                      onChange={(e) =>
                        setFormData({ ...formData, companyWebsite: e.target.value })
                      }
                      placeholder="company.ru"
                      type="text"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quiz-question">{QUESTION_LABEL}</Label>
                    <Textarea
                      id="quiz-question"
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      placeholder="Кратко опишите задачу или вопрос"
                      className="min-h-[88px] resize-y"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    * Обязательные поля: рабочая почта и сайт компании
                  </p>
                  <PersonalDataConsent
                    id="quiz-consent"
                    className="pt-2"
                    checked={formData.consent}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, consent: Boolean(checked) })
                    }
                  />
                  <MarketingConsent
                    id="quiz-marketing-consent"
                    className="pt-2"
                    checked={formData.marketingConsent}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, marketingConsent: Boolean(checked) })
                    }
                  />
                  <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold mb-4">{currentQuestion.question}</h3>

                {currentQuestion.type === 'radio' ? (
                  <RadioGroup
                    value={formData.patientsVolume}
                    onValueChange={handleRadioChange}
                  >
                    {currentQuestion.options.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.value} id={option.value} />
                        <Label
                          htmlFor={option.value}
                          className="font-normal cursor-pointer"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option) => {
                      const fieldName = currentQuestion.id === 2 ? 'problems' : 'expectedResults';
                      const isChecked = formData[fieldName].includes(option.value);

                      return (
                        <div key={option.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={option.value}
                            checked={isChecked}
                            onCheckedChange={(checked) =>
                              handleCheckboxChange(option.value, checked)
                            }
                          />
                          <Label
                            htmlFor={option.value}
                            className="font-normal cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Кнопки навигации */}
        <div className="flex justify-between items-center pt-4 border-t gap-4">
          <div className="flex gap-2">
            {currentStep > 1 && currentStep < totalSteps && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Назад
              </Button>
            )}
            {currentStep < totalSteps && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground"
              >
                Пропустить
              </Button>
            )}
          </div>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              className="flex items-center gap-2"
            >
              Далее
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepValid() || isSubmitting}
              size="lg"
            >
              {isSubmitting ? 'Отправка...' : 'Получить консультацию'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuizForm;

